/**
 * usePoints — Manages user gamification points, levels, and streaks.
 */
import { useCallback, useEffect, useState, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { logger } from '@/utils/logger';

export const POINTS = {
  COMPLETE_TASK: 5,
  COMPLETE_SUBTASK: 2,
  COMPLETE_GOAL: 30,
  CREATE_FIRST_GOAL: 10,
  ADD_TASK_WITH_DATE: 3,
  SHARE_GOAL: 5,
  JOIN_COMMON_GOAL: 5,
  COMPLETE_COMMON_GOAL: 20,
  DAILY_STREAK_3: 10,
  DAILY_STREAK_7: 25,
  DAILY_STREAK_30: 100,
  GENERATE_CERTIFICATE: 15,
  ADMIN_APPRECIATION: 10,
  CREATE_PLEDGE: 3,
  ACCEPT_PLEDGE: 5,
  SUBMIT_PROOF_FIRST: 10,
  PROOF_APPROVED_RECIPIENT: 25,
  PROOF_APPROVED_GIVER: 5,
  COMPLETE_PROFILE: 20,
  INSTALL_PWA: 100,
} as const;

const POINTS_PER_LEVEL = 100;

const levelSeenStorageKey = (userId: string) => `gt_last_seen_level:${userId}`;

const getStoredSeenLevel = (userId: string) => {
  try {
    const raw = localStorage.getItem(levelSeenStorageKey(userId));
    const n = raw ? Number(raw) : 0;
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
};

const setStoredSeenLevel = (userId: string, level: number) => {
  try {
    localStorage.setItem(levelSeenStorageKey(userId), String(level));
  } catch {
    // ignore
  }
};

interface UserPoints {
  total_points: number;
  level: number;
  current_streak_days: number;
  longest_streak_days: number;
  last_activity_date: string | null;
  points_to_next_level: number;
}

export const usePoints = () => {
  const [points, setPoints] = useState(0);
  const [level, setLevel] = useState(1);
  const [streakDays, setStreakDays] = useState(0);
  const [longestStreakDays, setLongestStreakDays] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showLevelUp, setShowLevelUp] = useState(false);
  const [newLevel, setNewLevel] = useState(1);
  const prevLevelRef = useRef(1);
  const { toast } = useToast();
  const { user } = useAuth();

  const migrateLocalPoints = useCallback(async () => {
    if (!user?.id) return;

    const localPoints = localStorage.getItem("user_points");
    if (localPoints && parseInt(localPoints) > 0) {
      try {
        await supabase.rpc("add_user_points", {
          p_user_id: user.id,
          p_points: parseInt(localPoints),
          p_action: "migration_from_local",
          p_description: "Migrated from localStorage",
        });
        localStorage.removeItem("user_points");
        logger.debug("Migrated", localPoints, "points from localStorage to database");
      } catch (error) {
        logger.error("Failed to migrate localStorage points:", error);
      }
    }
  }, [user?.id]);

  const fetchPoints = useCallback(async () => {
    const userId = user?.id;

    if (!userId) {
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase.rpc("get_user_points", {
        p_user_id: userId,
      });

      if (error) throw error;

      if (data) {
        const userPoints = data as unknown as UserPoints;
        setPoints(userPoints.total_points);
        setLevel(userPoints.level);
        setStreakDays(userPoints.current_streak_days);
        setLongestStreakDays(userPoints.longest_streak_days ?? 0);

        // Read raw value so we can distinguish "never seen on this origin" from "seen at level 0".
        // localStorage is per-origin, so a fresh preview origin would otherwise re-trigger the
        // Level Up modal for users who already leveled up in production.
        let seenLevelRaw: string | null = null;
        try { seenLevelRaw = localStorage.getItem(levelSeenStorageKey(userId)); } catch { /* ignore */ }
        const hasSeenLevel = seenLevelRaw !== null;
        const seenLevel = hasSeenLevel ? (Number(seenLevelRaw) || 0) : userPoints.level;
        const lastKnownLevel = Math.max(prevLevelRef.current, seenLevel);

        if (hasSeenLevel && userPoints.level > lastKnownLevel) {
          setNewLevel(userPoints.level);
          setShowLevelUp(true);
        }
        // Always persist current level so future real level-ups still trigger exactly once.
        setStoredSeenLevel(userId, userPoints.level);

        prevLevelRef.current = userPoints.level;
      }
    } catch (error) {
      logger.error("Error fetching points:", error);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (user?.id) {
      migrateLocalPoints();
      fetchPoints();
    } else {
      setLoading(false);
    }
  }, [user?.id, migrateLocalPoints, fetchPoints]);

  // Cross-component refresh bus: any feature that mutates points (e.g. the
  // Daily Offer popup edge function) can dispatch `points-refresh` to make
  // every mounted usePoints() instance re-pull the latest totals.
  useEffect(() => {
    if (!user?.id) return;
    const handler = () => { fetchPoints(); };
    window.addEventListener("points-refresh", handler);
    return () => window.removeEventListener("points-refresh", handler);
  }, [user?.id, fetchPoints]);

  // Queue streak milestones (idempotent — RPC blocks duplicates)
  useEffect(() => {
    if (!user?.id || streakDays <= 0) return;
    const queue = async (action: string, points: number, description: string) => {
      try {
        await supabase.rpc("queue_pending_reward", {
          p_action: action as any,
          p_points: points,
          p_reference_id: null,
          p_description: description,
        });
      } catch { /* idempotent fire-and-forget */ }
    };
    if (streakDays >= 3)  queue("daily_streak_3",  10,  "3-day streak");
    if (streakDays >= 7)  queue("daily_streak_7",  25,  "7-day streak");
    if (streakDays >= 30) queue("daily_streak_30", 100, "30-day streak");
  }, [user?.id, streakDays]);

  const awardPoints = useCallback(
    async (
      pointsToAward: number,
      action: string,
      referenceId?: string,
      description?: string
    ): Promise<boolean> => {
      if (!user?.id) {
        logger.warn("Cannot award points: user not authenticated");
        return false;
      }

      try {
        logger.debug(`Attempting to award ${pointsToAward} points for action: ${action}`);
        
        const { data, error } = await supabase.rpc("add_user_points", {
          p_user_id: user.id,
          p_points: pointsToAward,
          p_action: action as any,
          p_reference_id: referenceId || null,
          p_description: description || null,
        });

        if (error) {
          logger.error("RPC Error awarding points:", error);
          throw error;
        }

        const result = data as any;
        
        if (!result.success) {
          if (result.error === "duplicate") {
            logger.debug("Points already awarded for this action");
            return false;
          }
          logger.error("Points award failed:", result);
          return false;
        }

        setPoints(result.total_points);
        setLevel(result.level);
        logger.debug(`Points awarded. New total: ${result.total_points}, Level: ${result.level}`);

        await fetchPoints();

        // Global celebration bus — PointsCelebrationProvider listens.
        try {
          window.dispatchEvent(new CustomEvent("points-awarded", {
            detail: {
              amount: pointsToAward,
              reason: description || undefined,
              milestone: !!result.level_up,
            },
          }));
          // Cross-component refresh so every other mounted usePoints
          // re-pulls the latest totals (StatusStrip, header chips, etc).
          window.dispatchEvent(new CustomEvent("points-refresh"));
        } catch { /* non-fatal */ }

        if (result.level_up) {
          toast({
            title: "🎉 Level Up!",
            description: `You reached Level ${result.level}!`,
            duration: 5000,
          });
        }

        return true;
      } catch (error) {
        logger.error("Error awarding points:", error);
        return false;
      }
    },
    [user?.id, toast, fetchPoints]
  );

  const addTaskPoints = useCallback(
    async (taskId: string) => {
      const awarded = await awardPoints(POINTS.COMPLETE_TASK, "task_complete", taskId, "Completed a task");
      if (awarded) {
        toast({ title: "Task Completed!", description: `+${POINTS.COMPLETE_TASK} points`, duration: 2000 });
      }
      return awarded;
    },
    [awardPoints, toast]
  );

  // Phase 1.2 — Goal completion is now a *claimable* reward (queued, not auto-credited).
  // This makes the Claim chip light up and gives the user a satisfying "tap to receive" moment.
  const addGoalPoints = useCallback(
    async (goalId: string, goalTitle?: string) => {
      if (!user?.id) return false;
      try {
        const description = goalTitle
          ? `Completed "${goalTitle}"`
          : "You completed a goal";
        const { data, error } = await supabase.rpc("queue_pending_reward", {
          p_action: "goal_complete" as any,
          p_points: POINTS.COMPLETE_GOAL,
          p_reference_id: goalId,
          p_description: description,
        });
        if (error) {
          logger.warn("queue goal_complete error", error);
          return false;
        }
        const result = data as { success: boolean; error?: string; id?: string };
        if (result?.success) {
          // NOTE: goal completion already shows the GlobalCelebrationModal,
          // which now hosts the "Claim my +30 points" CTA. We therefore do NOT
          // fire the `reward-queued` event (would create a 2nd popup) and we
          // also suppress the RewardReadyPopup's query-count safety-net for a
          // short window so it doesn't pop afterwards either.
          try {
            (window as any).__suppressRewardPopupUntil = Date.now() + 15000;
          } catch { /* non-fatal */ }
          return true;
        }
        // duplicate or already_awarded — silent
        return false;
      } catch (e) {
        logger.error("addGoalPoints exception", e);
        return false;
      }
    },
    [user?.id]
  );

  const addCustomPoints = useCallback(
    async (pointsToAdd: number, action: string, referenceId?: string, reason?: string) => {
      const awarded = await awardPoints(pointsToAdd, action, referenceId, reason);
      if (awarded && reason) {
        toast({ title: "Points Earned!", description: `+${pointsToAdd} points for ${reason}`, variant: "default", duration: 2000 });
      }
      return awarded;
    },
    [awardPoints, toast]
  );

  return {
    points,
    level,
    streakDays,
    longestStreakDays,
    loading,
    addTaskPoints,
    addGoalPoints,
    addCustomPoints,
    awardPoints,
    fetchPoints,
    showLevelUp,
    setShowLevelUp,
    newLevel,
  };
};
